import { getLocale } from '@/helpers/locale-helper';
import { determineSignAlgorithm } from '@/sign/helpers/sign.helper';
import { SignPrivateKeyStrategy } from './sign-private-key.strategy';
import { SigningParams } from '@/sign/models/signing-params.model';
import { SignPurpose } from '@/sign/models/sign-constants';
import { setLoadingText } from '@/mixins/ui-mixin';
import { FileHashingResult, FileSigningResult } from '@/models/document-sign.models';
import { base64ToArrayBuffer } from '@/helpers';

export abstract class SignDocumentPrivateKeyStrategy extends SignPrivateKeyStrategy {
  protected documentVersionCode: string;
  private previousSigningResults: Array<FileSigningResult>;

  protected constructor(prms: any) {
    super();
    if (!prms) {
      throw new Error('Parameters are required');
    }
    this.documentVersionCode = prms.documentVersionCode;
    this.signPurpose = SignPurpose.Document;
    this.previousSigningResults = prms.previousSigningResults || [];
  }

  public async execute(prms: SigningParams): Promise<any> {
    await super.execute(prms);
    await this.init();
    await this.validate();
    await this.determineKeyTypes();

    setLoadingText({ text: getLocale('appLoading.prepareForSigningDocument').toString() });
    const hashes = await this.getHashes();

    this._fillInternalNestedSignFromPreviousSigning(hashes);

    setLoadingText({ text: getLocale('appLoading.signDocument').toString() });
    const signedHashes = await this.signHashes(hashes);

    return signedHashes;
  }

  protected async validate(): Promise<void> {
    await super.validate();

    if (!(await this.isKeyPrivateKeyReaded())) {
      throw new Error('Private key is not readed');
    }
  }

  protected async isKeyPrivateKeyReaded(): Promise<boolean> {
    return !!this.privateKeyContext;
  }

  protected async signHashes(signHashes: Array<FileHashingResult>): Promise<Array<FileSigningResult>> {
    if (!signHashes || !signHashes.length) {
      return Promise.reject(new Error(getLocale('appSign.notfoundError').toString()));
    }
    return this.signKeyTypeHashes(this.keyType, signHashes, this.euSignLib);
  }

  protected async signKeyTypeHashes(keyType: number, hashes: Array<FileHashingResult>, lib: any): Promise<Array<FileSigningResult>> {
    const signs: Array<FileSigningResult> = [];
    const signAlgo = determineSignAlgorithm(keyType);
    for (let index = 0; index < hashes.length; index++) {
      const element = hashes[index];

      let sign = new FileSigningResult({
        pDocumentVersionsFileCode: element.pDocumentVersionsFileCode,
        pSignBase64: null,
        isInternalNested: element.isInternalNested,
        documentVerFileSignDataId: element.documentVerFileSignDataId
      });

      if (element.isInternalNested) {
        const signContent = base64ToArrayBuffer(element.sign);
        sign.pSignBase64 = await this.signData(lib, signAlgo, signContent, false, true, true);
      } else {
        sign.pSignBase64 = await this.signHash(lib, signAlgo, element.hasheBase64, true, true);
      }

      signs.push(sign);
    }
    return signs;
  }

  protected signHash(
    lib: any,
    signAlgo: number,
    hash: string | Uint8Array | Array<string | Uint8Array>,
    appendCert: boolean,
    asBase64String: boolean): Promise<any> {
    return lib.CtxSignHash(this.privateKeyContext, signAlgo, hash, appendCert, asBase64String);
  }

  protected signData(
    lib: any,
    signAlgo: number,
    data: string | Uint8Array | Array<string | Uint8Array>,
    external: boolean,
    appendCert: boolean,
    asBase64String: boolean): Promise<any> {
    return lib.CtxSignData(this.privateKeyContext, signAlgo, data, external, appendCert, asBase64String);
  }

  protected abstract getHashes(): Promise<Array<FileHashingResult>>;

  private _fillInternalNestedSignFromPreviousSigning(hashes: Array<FileHashingResult>): void {
    if (!this.previousSigningResults.length) return;

    for (const hash of hashes) {
      if (!hash.isInternalNested) continue;

      let previousSigning = this.previousSigningResults
        .find(ps => ps.isInternalNested && ps.pDocumentVersionsFileCode === hash.pDocumentVersionsFileCode);
      if (previousSigning) {
        hash.sign = previousSigning.pSignBase64;
      } else {
        throw new Error('Previous signing result not found');
      }
    }
  }
}
